"use client";

import { BRAND_NAME } from "@/lib/marketing-seo";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { useTranslations } from "next-intl";
import type { ReactElement } from "react";

interface ComparisonRow {
  readonly label: string;
  readonly ours: string;
  readonly theirs: string;
  readonly hideOnMobile?: boolean;
}

const ROWS: readonly ComparisonRow[] = [
  {
    label: "Decision-Making",
    ours: "Short paths, small team & direct communication. 95% of your time goes to building.",
    theirs: "Lots of meetings, little code. Multiple layers of management.",
  },
  {
    label: "Leadership",
    ours: "Lean team, no middle management. And the CEO contributed 2 million LoC.",
    theirs: "Non-technical managers slow everything down.",
    hideOnMobile: true,
  },
  {
    label: "Ownership",
    ours: "Ownership from day one: you're responsible for real features from idea to release and get to see the impact.",
    theirs:
      "Working through small tickets – only management sees the bigger picture.",
  },
  {
    label: "Tech Stack & Legacy",
    ours: "Modern stack, no legacy. Our simple Infra-As-Code backend (Convex) makes changes easy.",
    theirs:
      "Grown legacy code and outdated technology that make every change slow and risky.",
  },
  {
    label: "AI-first Mentality",
    ours: "AI isn't a side project for us. It writes 100% of our code and you can use as many tokens as you need. Our entire product is centered around it.",
    theirs:
      "AI is either banned or you're expected to babysit the AI every step of the way.",
  },
];

/** "We know what matters to you as an engineer" — typical companies vs.
 * hausverwalter.ai. No outer card: only the brand column is highlighted.
 * Desktop: 3-column grid. Phone: label → theirs → ours. */
export function Engineering2Comparison(): ReactElement {
  const t = useTranslations("public.engineering2Comparison");
  return (
    <section id="compare" className="dvf-section c2-center">
      <div className="dvf-section-inner">
        <h2 className="dvf-section-title">
          We know what matters to you as an{" "}
          <span className="e2-accent">{t("engineer")}</span>.
        </h2>

        <div className="e2-vs-card">
          <div className="e2-vs-grid e2-vs-head">
            <div />
            <div className="e2-vs-head-cell e2-vs-head-theirs">
              {t("typicalSoftwareCompanies")}
            </div>
            <div className="e2-vs-head-cell e2-vs-ours">
              <span className="e2-vs-brand">
                <BrandLogoMark width={22} height={22} variant="website" />
                {BRAND_NAME}
              </span>
            </div>
          </div>

          {ROWS.map((row, index) => (
            <div
              className={[
                "e2-vs-grid e2-vs-row",
                index === ROWS.length - 1 ? "e2-vs-row-last" : "",
                row.hideOnMobile === true ? "e2-vs-row-desktop-only" : "",
              ]
                .filter((className: string) => className.length > 0)
                .join(" ")}
              key={row.label}
            >
              <div className="e2-vs-label">{row.label}</div>
              <div className="e2-vs-cell e2-vs-theirs">
                <span className="e2-vs-mini">{t("typicalCompanies")}</span>
                <p className="e2-vs-text">{row.theirs}</p>
              </div>
              <div className="e2-vs-cell e2-vs-ours">
                <span className="e2-vs-mini">{BRAND_NAME}</span>
                <p className="e2-vs-text">{row.ours}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
